import { useCallback, useEffect, useRef, useState } from "react";
import {
  effectiveIncludeNsfw,
  getHubItemDisplayLabel,
  getHubItemParentDependencyLabel,
  hubItemKindForDisplay,
  normalizeHubItemCreator,
  normalizeHubItemStats,
  type HubComment,
  type HubItemDisplayInput,
  type HubItemDto,
  type HubItemKind,
  type HubListing,
  type HubProfile,
  type HubQuery,
  type HubSort,
  type Settings,
} from "@roqeet/core";
import { storage } from "../../state/storage.js";
import { hubClient, signInToHub, signOutFromHub } from "../../state/hub.js";
import { installListing } from "../../state/hub-actions.js";
import {
  hubItemDisplayInput,
  hubItemRating,
  loadHubCardPage,
  type HubCardViewModel,
} from "../../platform/hub/hub-card-view-model.js";
import { PublishDialog } from "./PublishDialog.js";
import { useT } from "../../i18n/use-t.js";
import type { TKey } from "../../i18n/strings.js";
import { useEscapeToClose } from "../use-escape-to-close.js";
import { Download, Star, MessageCircle, X, User, Book, Sliders, Gamepad2, Package, Eye, Bookmark, Bot, Save, Search, Compass, Plus, LogOut, Globe } from "lucide-react";

/**
 * RoQeet Hub 화면 — 결정 #23/#31. 캐릭터·로어북·프리셋·게임 카드를 검색·다운로드·
 * 발행하고, 평점·댓글·팔로우(소셜 레이어)를 제공한다.
 */

const HUB_ITEM_KIND_OPTIONS: Array<HubItemKind | "all"> = [
  "all",
  "game",
  "bot",
  "persona",
  "lorebook",
  "setting_preset",
  "save_file",
];

const SORT_LABEL_KEYS: Record<HubSort, TKey> = {
  recent: "comm.sort.recent",
  popular: "comm.sort.popular",
  "top-rated": "comm.sort.topRated",
  downloads: "comm.sort.downloads",
  views: "comm.sort.views",
  bookmarks: "comm.sort.bookmarks",
};

const KIND_STYLES: Record<string, { bg: string; text: string; border: string }> = {
  game: { bg: "bg-emerald-500/10", text: "text-emerald-600 dark:text-emerald-400", border: "border-emerald-500/20" },
  bot: { bg: "bg-purple-500/10", text: "text-purple-600 dark:text-purple-400", border: "border-purple-500/20" },
  persona: { bg: "bg-pink-500/10", text: "text-pink-600 dark:text-pink-400", border: "border-pink-500/20" },
  lorebook: { bg: "bg-amber-500/10", text: "text-amber-600 dark:text-amber-400", border: "border-amber-500/20" },
  setting_preset: { bg: "bg-blue-500/10", text: "text-blue-600 dark:text-blue-400", border: "border-blue-500/20" },
  save_file: { bg: "bg-cyan-500/10", text: "text-cyan-600 dark:text-cyan-400", border: "border-cyan-500/20" },
};

function iconFor(item: HubItemDisplayInput, size = 32): React.ReactNode {
  const cn = "text-ink-soft stroke-[1.5] shrink-0";
  switch (hubItemKindForDisplay(item)) {
    case "game": return <Gamepad2 size={size} className={cn} aria-hidden="true" />;
    case "bot": return <Bot size={size} className={cn} aria-hidden="true" />;
    case "persona": return <User size={size} className={cn} aria-hidden="true" />;
    case "lorebook": return <Book size={size} className={cn} aria-hidden="true" />;
    case "setting_preset": return <Sliders size={size} className={cn} aria-hidden="true" />;
    case "save_file": return <Save size={size} className={cn} aria-hidden="true" />;
    default: return <Package size={size} className={cn} aria-hidden="true" />;
  }
}

function iconForKind(kind: HubItemKind | "all", size = 12): React.ReactNode {
  const cn = "shrink-0";
  switch (kind) {
    case "all": return <Compass size={size} className={cn} />;
    case "game": return <Gamepad2 size={size} className={cn} />;
    case "bot": return <Bot size={size} className={cn} />;
    case "persona": return <User size={size} className={cn} />;
    case "lorebook": return <Book size={size} className={cn} />;
    case "setting_preset": return <Sliders size={size} className={cn} />;
    case "save_file": return <Save size={size} className={cn} />;
    default: return <Package size={size} className={cn} />;
  }
}

function renderSubtypeBadge(item: HubItemDisplayInput, locale: string | undefined) {
  const displayKind = hubItemKindForDisplay(item);
  const label = getHubItemDisplayLabel(item, locale);
  const styles = KIND_STYLES[displayKind] || { bg: "bg-bg-raised", text: "text-ink-soft", border: "border-line" };

  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold border ${styles.bg} ${styles.text} ${styles.border}`}>
      {iconFor(item, 10)}
      <span>{label}</span>
    </span>
  );
}

export function HubView() {
  const { locale, t } = useT();
  const [profile, setProfile] = useState<HubProfile | null>(hubClient.currentProfile());
  const [settings, setSettings] = useState<Settings | undefined>();
  const [itemKind, setItemKind] = useState<HubItemKind | "all">("all");
  const [sort, setSort] = useState<HubSort>("recent");
  const [text, setText] = useState("");
  const [submittedText, setSubmittedText] = useState("");
  const [cards, setCards] = useState<HubCardViewModel[]>([]);
  const [nextCursor, setNextCursor] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [previewItem, setPreviewItem] = useState<HubItemDto | null>(null);
  const [adminPanelOpen, setAdminPanelOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);

  const configured = hubClient.isConfigured();

  useEffect(() => {
    hubClient.subscribe(setProfile);
  }, []);
  
  useEffect(() => {
    void storage.getSettings().then(setSettings);
  }, []);

  useEffect(() => {
    if (profile) {
      hubClient.checkAdmin().then(setIsAdmin).catch(() => setIsAdmin(false));
    } else {
      setIsAdmin(false);
    }
  }, [profile]);

  const includeNsfw = effectiveIncludeNsfw(!!settings?.ageVerified, !!settings?.showNsfw);

  const runQuery = useCallback(
    async (reset: boolean) => {
      if (!configured) return;
      setLoading(true);
      setError(null);
      try {
        const query: HubQuery = {
          ...(submittedText ? { text: submittedText } : {}),
          ...(itemKind !== "all" ? { itemKind } : {}),
          sort,
          includeNsfw,
          ...(reset ? {} : nextCursor ? { cursor: nextCursor } : {}),
        };
        const page = await loadHubCardPage(hubClient, query, includeNsfw);
        setCards((prev) => (reset ? page.items : [...prev, ...page.items]));
        setNextCursor(page.nextCursor);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setLoading(false);
      }
    },
    [configured, submittedText, itemKind, sort, includeNsfw, nextCursor],
  );

  useEffect(() => {
    if (!configured || !settings) return;
    void runQuery(true);
  }, [configured, settings?.ageVerified, settings?.showNsfw, itemKind, sort, submittedText]);

  useEffect(() => {
    if (notice || error) {
      const timer = setTimeout(() => {
        setNotice(null);
        setError(null);
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [notice, error]);

  const signIn = async () => {
    setError(null);
    try {
      await signInToHub();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const install = async (card: HubCardViewModel) => {
    if (!card.legacyListing) return;
    setBusyId(card.item.id);
    setNotice(null);
    setError(null);
    try {
      const outcome = await installListing(card.legacyListing.id);
      setNotice(t("comm.installedToLibrary", { title: outcome.title }));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyId(null);
    }
  };

  if (!configured) {
    return <NotConfigured />;
  }

  return (
    <div className="flex h-full flex-col bg-bg">
      {detailId && (
        <ListingDetailDialog
          listingId={detailId}
          profile={profile}
          onClose={() => setDetailId(null)}
          onInstalled={(title) => {
            setNotice(t("comm.installed", { title }));
            setDetailId(null);
          }}
          onSignIn={signIn}
        />
      )}
      {previewItem && (
        <HubItemPreviewDialog
          item={previewItem}
          onClose={() => setPreviewItem(null)}
        />
      )}
      {publishOpen && (
        <PublishDialog
          onClose={() => setPublishOpen(false)}
          onPublished={(title) => {
            setPublishOpen(false);
            setNotice(t("comm.published", { title }));
            void runQuery(true);
          }}
        />
      )}
      {adminPanelOpen && isAdmin && (
        <AdminPanelDialog onClose={() => setAdminPanelOpen(false)} />
      )}

      {/* 헤더: 로그인 상태 + 발행 */}
      <div className="flex items-center justify-between gap-4 border-b border-line bg-bg-soft/20 px-4 py-3">
        <div className="flex min-w-0 items-center gap-2">
          {profile ? (
            <div className="flex items-center gap-2 rounded-full bg-accent/10 border border-accent/20 px-3 py-1 text-xs text-accent-soft font-medium">
              <User size={13} className="shrink-0 text-accent" />
              <span className="truncate">@{profile.handle}</span>
            </div>
          ) : (
            <div className="flex items-center gap-2 rounded-full bg-bg-raised border border-line px-3 py-1 text-xs text-ink-soft">
              <Globe size={13} className="shrink-0 text-ink-faint" />
              <span className="truncate">{t("comm.browseWithoutLogin")}</span>
            </div>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {profile ? (
            <>
              {isAdmin && (
                <button
                  className="flex items-center gap-1.5 rounded-lg border border-line px-3 py-1.5 text-xs text-ink-soft hover:bg-bg-raised hover:text-ink transition"
                  onClick={() => setAdminPanelOpen(true)}
                >
                  {t("comm.admin.title")}
                </button>
              )}
              <button
                className="flex items-center gap-1 rounded-lg bg-accent px-3 py-1.5 text-xs font-semibold text-white hover:bg-accent-soft hover:shadow-lg hover:shadow-accent/10 active:scale-95 transition"
                onClick={() => setPublishOpen(true)}
              >
                <Plus size={14} />
                <span>{t("comm.publish")}</span>
              </button>
              <button
                className="flex items-center gap-1 rounded-lg border border-line px-3 py-1.5 text-xs text-ink-soft hover:bg-red-500/10 hover:text-red-400 hover:border-red-500/20 transition"
                onClick={() => void signOutFromHub()}
              >
                <LogOut size={13} />
                <span>{t("comm.signOut")}</span>
              </button>
            </>
          ) : (
            <button
              className="flex items-center gap-1 rounded-lg bg-accent px-4 py-1.5 text-xs font-semibold text-white hover:bg-accent-soft hover:shadow-lg hover:shadow-accent/10 active:scale-95 transition"
              onClick={signIn}
            >
              <span>{t("comm.signIn")}</span>
            </button>
          )}
        </div>
      </div>

      {/* 검색 + 필터 */}
      <div className="space-y-3 border-b border-line bg-bg-soft/10 p-4">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setSubmittedText(text.trim());
          }}
          className="relative flex items-center"
        >
          <Search size={15} className="absolute left-3.5 text-ink-faint pointer-events-none" />
          <input
            className="w-full rounded-xl border border-line bg-bg-soft pl-10 pr-10 py-2.5 text-xs text-ink placeholder:text-ink-faint focus:border-accent/60 focus:bg-bg/40 outline-none transition"
            placeholder={t("comm.searchPlaceholder")}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          {text.trim() && (
            <button
              type="button"
              onClick={() => {
                setText("");
                setSubmittedText("");
              }}
              className="absolute right-3.5 p-1 rounded-md text-ink-faint hover:bg-bg-raised hover:text-ink transition"
            >
              <X size={12} />
            </button>
          )}
        </form>

        <div className="flex flex-wrap gap-1.5">
          {HUB_ITEM_KIND_OPTIONS.map((k) => (
            <Chip key={k} active={itemKind === k} onClick={() => setItemKind(k)}>
              <span className="flex items-center gap-1.5">
                {iconForKind(k, 12)}
                <span>{k === "all" ? t("comm.kind.all") : getHubItemDisplayLabel({ itemKind: k }, locale)}</span>
              </span>
            </Chip>
          ))}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-line/40 pt-2.5 mt-1">
          <div className="flex items-center gap-2 text-xs">
            <span className="text-ink-faint text-[11px] font-medium mr-1 select-none">정렬</span>
            <div className="flex items-center gap-1 bg-bg-soft/50 border border-line rounded-lg p-0.5">
              {(Object.keys(SORT_LABEL_KEYS) as HubSort[]).map((s) => (
                <button
                  key={s}
                  className={[
                    "px-2.5 py-1 text-[11px] font-medium rounded-md transition",
                    sort === s
                      ? "bg-bg-raised text-ink shadow-sm ring-1 ring-black/5"
                      : "text-ink-soft hover:text-ink hover:bg-bg-raised/35",
                  ].join(" ")}
                  onClick={() => setSort(s)}
                >
                  {t(SORT_LABEL_KEYS[s])}
                </button>
              ))}
            </div>
          </div>
          {!includeNsfw && (
            <span className="shrink-0 text-[10px] text-ink-faint bg-bg-raised/40 px-2 py-0.5 rounded-full border border-line/30" title={t("comm.sfwOnlyTitle")}>
              🛡️ {t("comm.sfwOnly")}
            </span>
          )}
        </div>
      </div>

      {/* Toast Notification */}
      {(notice || error) && (
        <div className="fixed bottom-6 right-6 z-50 flex max-w-sm animate-in slide-in-from-bottom-5 fade-in duration-300">
          <div
            className={[
              "flex items-start gap-3 rounded-xl border p-4 shadow-xl backdrop-blur-md",
              error ? "bg-red-950/90 border-red-500/30 text-red-200" : "bg-bg/90 border-line text-ink",
            ].join(" ")}
          >
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium leading-relaxed">{error ?? notice}</p>
            </div>
            <button
              className="shrink-0 text-ink-faint hover:text-ink transition"
              onClick={() => {
                setError(null);
                setNotice(null);
              }}
            >
              <X size={16} aria-hidden="true" />
            </button>
          </div>
        </div>
      )}

      {/* 목록 */}
      <div className="min-h-0 flex-1 overflow-y-auto p-4 scroll-touch">
        {cards.length === 0 && !loading && (
          <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-line px-4 py-16 text-center">
            <Package size={32} className="text-ink-faint mb-3 stroke-[1.2]" />
            <p className="text-xs text-ink-faint leading-relaxed max-w-xs">
              {error ? t("comm.loadFailed") : t("comm.empty")}
            </p>
          </div>
        )}

        <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {cards.map((card) => {
            const item = card.item;
            const legacy = card.legacyListing;
            const displayInput = hubItemDisplayInput(item);
            const parentLabel = item.parent
              ? `Parent: ${item.parent.title}`
              : getHubItemParentDependencyLabel(displayInput, locale);
            const authorLabel = card.creator.handle ?? card.creator.displayName;
            const rating = hubItemRating(item);
            const installUnavailableLabel = "Coming soon";
            return (
              <li
                key={item.id}
                onClick={() => {
                  if (card.canDownload) setDetailId(item.id);
                  else setPreviewItem(item);
                }}
                className="group flex gap-4 rounded-xl border border-line bg-bg-soft/50 p-4 hover:border-accent/40 hover:bg-bg-soft hover:shadow-lg hover:shadow-black/5 transition duration-200 cursor-pointer"
              >
              <Thumbnail item={item} />
              <div className="min-w-0 flex-1 flex flex-col justify-between">
                <div>
                  <div className="flex items-start justify-between gap-1">
                    <span className="truncate text-sm font-semibold text-ink group-hover:text-accent-soft transition">{item.title}</span>
                    <div className="flex gap-1 shrink-0">
                      {rating === "nsfw" && (
                        <span className="shrink-0 rounded bg-red-500/15 border border-red-500/20 px-1 py-0.5 text-[9px] font-bold text-red-400">
                          {t("comm.adult")}
                        </span>
                      )}
                      {legacy?.sourceVisibility === "hidden" && (
                        <span className="shrink-0 rounded bg-bg-raised border border-line px-1 py-0.5 text-[9px] text-ink-faint" title={t("comm.sourceLocked")}>
                          🔒
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                    {renderSubtypeBadge(displayInput, locale)}
                    {parentLabel && (
                      <span className="text-[10px] text-ink-faint bg-bg-raised px-1 py-0.5 rounded border border-line/50">
                        {parentLabel}
                      </span>
                    )}
                    <button
                      className="text-[10px] text-accent-soft hover:underline font-medium"
                      onClick={(e) => {
                        e.stopPropagation();
                          setText("@" + authorLabel);
                          setSubmittedText("@" + authorLabel);
                        }}
                      >
                        @{authorLabel}
                      </button>
                  </div>

                  {item.summary && (
                    <p className="mt-2 line-clamp-2 text-xs text-ink-soft leading-relaxed pr-2">{item.summary}</p>
                  )}
                </div>

                <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-line/30 pt-2.5">
                  <div className="flex items-center gap-2.5 text-[10px] text-ink-faint">
                      <span title={t("comm.views")} className="flex items-center gap-0.5"><Eye size={12} className="shrink-0" /> {card.stats.views}</span>
                      <span title={t("comm.downloads")} className="flex items-center gap-0.5"><Download size={12} className="shrink-0" /> {card.stats.downloads}</span>
                      <span title={t("comm.bookmarks")} className="flex items-center gap-0.5"><Bookmark size={12} className="shrink-0" /> {card.stats.bookmarks}</span>
                      <span title={t("comm.rating")} className="flex items-center gap-0.5">
                        <Star size={12} className={`shrink-0 ${card.stats.ratingCount > 0 && card.stats.ratingAverage != null ? "text-yellow-400 fill-current" : ""}`} />
                        {card.stats.ratingCount > 0 && card.stats.ratingAverage != null ? card.stats.ratingAverage.toFixed(1) : "-"}
                      </span>
                  </div>

                  <div className="flex items-center gap-2">
                    {card.canDownload ? (
                      <button
                        className="flex items-center gap-1 rounded bg-accent/10 border border-accent/20 px-2.5 py-1 text-xs font-semibold text-accent-soft hover:bg-accent hover:text-white hover:border-transparent active:scale-95 transition"
                        onClick={(e) => {
                          e.stopPropagation();
                          void install(card);
                        }}
                        disabled={busyId === item.id}
                      >
                        <Download size={12} />
                        <span>{busyId === item.id ? t("comm.installing") : t("comm.install")}</span>
                      </button>
                    ) : (
                      <button
                        className="flex items-center gap-1 rounded border border-line bg-bg-raised/40 px-2.5 py-1 text-xs font-semibold text-ink-faint"
                        onClick={(e) => e.stopPropagation()}
                        disabled
                      >
                        <Download size={12} />
                        <span>{installUnavailableLabel}</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </li>
            );
          })}
        </ul>

        {loading && (
          <div className={`grid grid-cols-1 gap-3 sm:grid-cols-2 ${cards.length > 0 ? "mt-3" : ""}`}>
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </div>
        )}

        {!loading && nextCursor && (
          <div className="py-6 text-center">
            <button
              className="rounded-xl border border-line bg-bg-soft/40 px-5 py-2 text-xs font-semibold text-ink-soft hover:bg-bg-raised hover:text-ink transition"
              onClick={() => void runQuery(false)}
            >
              {t("comm.loadMore")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function NotConfigured() {
  const { t } = useT();
  const bodyParts = t("comm.notConfigured.body").split(/(\{url\}|\{anon\}|\{readme\})/g);
  return (
    <div className="flex h-full items-center justify-center p-6 bg-bg/50">
      <div className="max-w-md rounded-2xl border border-line bg-bg-soft p-6 shadow-xl relative overflow-hidden">
        <div className="absolute -right-10 -top-10 w-32 h-32 bg-accent/5 rounded-full blur-2xl pointer-events-none" />

        <div className="flex flex-col items-center text-center">
          <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-accent/10 text-accent ring-1 ring-accent/20">
            <Package size={24} className="stroke-[1.5]" />
          </div>

          <h2 className="text-base font-semibold text-ink tracking-tight">
            {t("comm.notConfigured.title")}
          </h2>

          <div className="mt-4 rounded-xl bg-bg-raised/50 border border-line/50 p-4 text-left">
            <h3 className="text-xs font-bold text-ink-soft mb-1 flex items-center gap-1">
              <span>📦</span> 로컬 작업 공간 모드로 실행 중
            </h3>
            <p className="text-[11px] text-ink-faint leading-relaxed">
              {t("comm.notConfigured.fileShareNote")}
            </p>
          </div>

          <div className="mt-4 text-xs leading-relaxed text-ink-soft bg-bg/40 p-3.5 rounded-xl border border-line/30 w-full text-left">
            <span className="block font-semibold text-ink mb-1 text-[11px]">RoQeet Hub 연동 방법:</span>
            <span className="text-[11px] text-ink-faint leading-relaxed">
              {bodyParts.map((part, i) => {
                if (part === "{url}")
                  return <code key={i} className="font-mono text-[10px] bg-bg-raised border border-line px-1.5 py-0.5 rounded text-accent-soft mx-0.5">VITE_SUPABASE_URL</code>;
                if (part === "{anon}")
                  return <code key={i} className="font-mono text-[10px] bg-bg-raised border border-line px-1.5 py-0.5 rounded text-accent-soft mx-0.5">VITE_SUPABASE_ANON_KEY</code>;
                if (part === "{readme}")
                  return <code key={i} className="font-mono text-[10px] bg-bg-raised border border-line px-1.5 py-0.5 rounded text-ink mx-0.5">infra/supabase/README.md</code>;
                return part;
              })}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      className={[
        "rounded-lg px-2.5 py-1 text-[11px] font-medium border transition duration-150 active:scale-95",
        active
          ? "bg-accent/15 border-accent text-accent-soft shadow-sm shadow-accent/5"
          : "border-line text-ink-soft hover:border-ink-faint hover:bg-bg-raised/50",
      ].join(" ")}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

function Thumbnail({ item, className = "h-20 w-20" }: { item: HubItemDto | HubListing; className?: string }) {
  const displayInput = "ownerId" in item ? hubItemDisplayInput(item) : item;
  const displayKind = hubItemKindForDisplay(displayInput);
  if (item.thumbnailUrl) {
    return (
      <img
        src={item.thumbnailUrl}
        alt=""
        className={`${className} shrink-0 rounded-lg object-cover border border-line`}
        loading="lazy"
      />
    );
  }

  const gradientStyles: Record<string, string> = {
    game: "from-emerald-600/20 to-emerald-900/10 text-emerald-400 ring-emerald-500/20",
    bot: "from-purple-600/20 to-purple-900/10 text-purple-400 ring-purple-500/20",
    persona: "from-pink-600/20 to-pink-900/10 text-pink-400 ring-pink-500/20",
    lorebook: "from-amber-600/20 to-amber-900/10 text-amber-400 ring-amber-500/20",
    setting_preset: "from-blue-600/20 to-blue-900/10 text-blue-400 ring-blue-500/20",
    save_file: "from-cyan-600/20 to-cyan-900/10 text-cyan-400 ring-cyan-500/20",
  };
  const gradient = gradientStyles[displayKind] || "from-bg-raised to-line text-ink-soft ring-line";

  return (
    <div className={`flex ${className} shrink-0 items-center justify-center rounded-lg bg-gradient-to-br ${gradient} border border-line shadow-inner`}>
      {iconFor(displayInput, className === "h-20 w-20" ? 28 : 36)}
    </div>
  );
}

function SkeletonCard() {
  return (
    <div className="flex gap-4 rounded-xl border border-line bg-bg-soft/20 p-4 animate-pulse">
      <div className="h-20 w-20 shrink-0 rounded-lg bg-bg-raised/50"></div>
      <div className="min-w-0 flex-1 flex flex-col justify-between">
        <div>
          <div className="h-4 w-1/2 bg-bg-raised/50 rounded mb-2"></div>
          <div className="flex gap-1.5 mb-3">
            <div className="h-3 w-12 bg-bg-raised/50 rounded"></div>
            <div className="h-3 w-16 bg-bg-raised/50 rounded"></div>
          </div>
          <div className="h-2.5 w-full bg-bg-raised/50 rounded mb-1.5"></div>
          <div className="h-2.5 w-3/4 bg-bg-raised/50 rounded"></div>
        </div>
        <div className="mt-3 flex items-center justify-between border-t border-line/30 pt-2.5">
          <div className="h-2.5 w-24 bg-bg-raised/50 rounded"></div>
          <div className="h-6 w-16 bg-bg-raised/50 rounded"></div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 상세 다이얼로그(페이로드 다운로드 + 평점 + 댓글)
// ---------------------------------------------------------------------------

function HubItemPreviewDialog({
  item,
  onClose,
}: {
  item: HubItemDto;
  onClose: () => void;
}) {
  const { locale } = useT();
  const displayInput = hubItemDisplayInput(item);
  const parentLabel = getHubItemParentDependencyLabel(displayInput, locale);
  const parentSummary = item.parent ? `Parent: ${item.parent.title}` : parentLabel;
  const creator = normalizeHubItemCreator(item.ownerId, item.creator);
  const stats = normalizeHubItemStats(item.stats);
  const creatorLabel = creator.handle ? `@${creator.handle}` : creator.displayName;
  const hasRating = stats.ratingCount > 0 && stats.ratingAverage != null;
  const ratingLabel = hasRating ? stats.ratingAverage!.toFixed(1) : "-";

  return (
    <Modal onClose={onClose} title={item.title} size="xl">
      <div className="space-y-5">
        <div className="flex gap-4 items-start">
          <Thumbnail item={item} className="h-16 w-16" />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-1.5">
              {renderSubtypeBadge(displayInput, locale)}
              {parentSummary && (
                <span className="text-[10px] text-ink-faint bg-bg-raised px-1 py-0.5 rounded border border-line/50">
                  {parentSummary}
                </span>
              )}
            </div>
            <p className="mt-2 text-xs text-ink-faint">{creatorLabel}</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 text-xs text-ink-faint">
          <span className="flex items-center gap-1"><Eye size={13} /> {stats.views}</span>
          <span className="flex items-center gap-1"><Download size={13} /> {stats.downloads}</span>
          <span className="flex items-center gap-1"><Bookmark size={13} /> {stats.bookmarks}</span>
          <span className="flex items-center gap-1">
            <Star size={13} className={hasRating ? "text-yellow-400 fill-current" : ""} />
            {ratingLabel}
          </span>
        </div>

        {item.description || item.summary ? (
          <div className="rounded-xl border border-line/40 bg-bg-soft/20 p-4">
            <p className="whitespace-pre-wrap text-sm leading-relaxed text-ink-soft">
              {item.description ?? item.summary}
            </p>
          </div>
        ) : (
          <p className="text-xs text-ink-faint">No description is available for this Hub item yet.</p>
        )}

        {item.tags && item.tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {item.tags.map((tag) => (
              <span key={tag} className="rounded-lg bg-bg-raised border border-line px-2.5 py-1 text-[11px] font-medium text-ink-soft">
                #{tag}
              </span>
            ))}
          </div>
        )}

        <div className="rounded-xl border border-line bg-bg-raised/30 px-3 py-2 text-xs text-ink-faint">
          Install/download is not available for this Hub item yet.
        </div>
      </div>
    </Modal>
  );
}

function ListingDetailDialog({
  listingId,
  profile,
  onClose,
  onInstalled,
  onSignIn,
}: {
  listingId: string;
  profile: HubProfile | null;
  onClose: () => void;
  onInstalled: (title: string) => void;
  onSignIn: () => void;
}) {
  const { locale, t } = useT();
  const [listing, setListing] = useState<HubListing | null>(null);
  const [comments, setComments] = useState<HubComment[]>([]);
  const [commentText, setCommentText] = useState("");
  const [myStars, setMyStars] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [installing, setInstalling] = useState(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const payloadRef = useRef<string | null>(null);

  useEffect(() => {
    let live = true;
    setLoading(true);
    (async () => {
      try {
        const [detail, commentPage] = await Promise.all([
          hubClient.download(listingId),
          hubClient.listComments(listingId),
        ]);
        if (!live) return;
        payloadRef.current = detail.payload;
        setListing(detail);
        setComments(commentPage.items);
        if (typeof detail.viewerRating === "number") setMyStars(detail.viewerRating);
      } catch (e) {
        if (live) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => {
      live = false;
    };
  }, [listingId]);

  const recordView = useCallback(() => {
    let called = false;
    return () => {
      if (!called) {
        called = true;
        void hubClient.incrementView(listingId);
      }
    };
  }, [listingId]);

  useEffect(() => recordView()(), [recordView]);

  const install = async () => {
    if (!listing) return;
    setInstalling(true);
    setError(null);
    try {
      const outcome = await installListing(listing.id);
      onInstalled(outcome.title);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setInstalling(false);
    }
  };

  const rate = async (stars: number) => {
    if (!profile) return onSignIn();
    setError(null);
    const prev = myStars;
    setMyStars(stars);
    try {
      await hubClient.rate(listingId, stars);
    } catch (e) {
      setMyStars(prev);
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const toggleBookmark = async () => {
    if (!profile) return onSignIn();
    if (!listing) return;
    setError(null);
    const prev = !!listing.viewerBookmarked;
    try {
      setListing({ ...listing, viewerBookmarked: !prev, bookmarkCount: listing.bookmarkCount + (prev ? -1 : 1) });
      if (prev) await hubClient.unbookmark(listingId);
      else await hubClient.bookmark(listingId);
    } catch (e) {
      setListing({ ...listing, viewerBookmarked: prev, bookmarkCount: listing.bookmarkCount });
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const unpublish = async () => {
    if (!profile || !listing || profile.id !== listing.author.id) return;
    if (!confirm(t("comm.unpublishConfirm"))) return;
    setWorking(true);
    try {
      await hubClient.unpublish(listingId);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setWorking(false);
    }
  };

  const addComment = async () => {
    if (!profile) return onSignIn();
    const body = commentText.trim();
    if (!body) return;
    setError(null);
    try {
      const created = await hubClient.addComment(listingId, body);
      setComments((prev) => [created, ...prev]);
      setCommentText("");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const removeComment = async (id: string) => {
    setError(null);
    try {
      await hubClient.deleteComment(id);
      setComments((prev) => prev.filter((c) => c.id !== id));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <Modal onClose={onClose} title={listing?.title ?? t("comm.details")} size="3xl">
      {loading ? (
        <div className="flex flex-col items-center justify-center py-12 text-ink-faint">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-accent border-t-transparent mb-3" />
          <p className="text-sm">{t("common.loading")}</p>
        </div>
      ) : !listing ? (
        <p className="p-4 text-sm text-red-300">{error ?? t("comm.loadFailedShort")}</p>
      ) : (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {/* Left Main Column */}
          <div className="md:col-span-2 space-y-5">
            <div className="flex gap-4 items-start">
              <Thumbnail item={listing} className="h-16 w-16" />
              <div>
                <div className="flex items-center flex-wrap gap-2 text-xs text-ink-faint">
                  <span className="font-medium text-ink-soft">@{listing.author.handle}</span>
                  {listing.version && <span>· v{listing.version}</span>}
                  {listing.license && <span>· {listing.license}</span>}
                  {listing.rating === "nsfw" && (
                    <span className="rounded bg-red-500/15 border border-red-500/20 px-1.5 py-0.5 text-[10px] font-semibold text-red-400">{t("comm.adult")}</span>
                  )}
                  {listing.sourceVisibility === "hidden" && (
                    <span className="rounded bg-bg-raised border border-line px-1.5 py-0.5 text-[10px] text-ink-faint">{t("comm.sourceLocked")}</span>
                  )}
                </div>
              </div>
            </div>

            {listing.description ? (
              <div className="bg-bg-soft/20 border border-line/40 rounded-xl p-4">
                <p className="whitespace-pre-wrap text-sm leading-relaxed text-ink-soft">{listing.description}</p>
              </div>
            ) : (
              <p className="text-xs text-ink-faint italic">등록된 상세 설명이 없습니다.</p>
            )}

            {listing.tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5 pt-1">
                {listing.tags.map((tag) => (
                  <span key={tag} className="rounded-lg bg-bg-raised border border-line px-2.5 py-1 text-[11px] font-medium text-ink-soft">
                    #{tag}
                  </span>
                ))}
              </div>
            )}

            {/* 댓글 영역 */}
            <div className="border-t border-line/50 pt-5">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-ink-faint">
                  {t("comm.commentsWithCount", { count: comments.length })}
                </h3>
              </div>

              <div className="flex gap-2">
                <input
                  className="min-w-0 flex-1 rounded-xl border border-line bg-bg-soft px-3.5 py-2 text-xs text-ink placeholder:text-ink-faint focus:border-accent/60 outline-none transition"
                  placeholder={profile ? t("comm.commentPlaceholder") : t("comm.commentPlaceholderLoggedOut")}
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") void addComment();
                  }}
                  disabled={!profile}
                />
                <button
                  className="shrink-0 rounded-xl bg-bg-raised border border-line px-4 py-2 text-xs font-semibold text-ink-soft hover:bg-line transition disabled:opacity-50"
                  onClick={() => void addComment()}
                  disabled={!profile}
                >
                  {t("comm.post")}
                </button>
              </div>

              <ul className="mt-4 space-y-2.5 max-h-80 overflow-y-auto pr-1">
                {comments.map((c) => (
                  <li key={c.id} className="rounded-xl border border-line bg-bg-soft/40 p-3 flex flex-col gap-1 text-xs">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold text-accent-soft">@{c.author.handle}</span>
                      {c.viewerIsAuthor && (
                        <button
                          className="text-[10px] text-red-400 hover:underline transition"
                          onClick={() => void removeComment(c.id)}
                        >
                          {t("common.delete")}
                        </button>
                      )}
                    </div>
                    <p className="whitespace-pre-wrap text-ink-soft leading-relaxed">{c.body}</p>
                  </li>
                ))}
                {comments.length === 0 && (
                  <li className="py-8 text-center text-xs text-ink-faint border border-dashed border-line rounded-xl">
                    💬 {t("comm.firstComment")}
                  </li>
                )}
              </ul>
            </div>
          </div>

          {/* Right Sidebar Column */}
          <div className="space-y-4">
            <div className="rounded-2xl border border-line bg-bg-soft/50 p-4 space-y-4 shadow-sm">
              <div className="flex justify-center pb-2">
                <Thumbnail item={listing} />
              </div>

              {/* Install Action */}
              <div className="space-y-2">
                <button
                  className="flex w-full items-center justify-center gap-1.5 rounded-xl bg-accent px-4 py-2.5 text-xs font-semibold text-white hover:bg-accent-soft hover:shadow-lg hover:shadow-accent/10 active:scale-[0.98] transition disabled:opacity-50"
                  onClick={install}
                  disabled={installing}
                >
                  <Download size={14} />
                  <span>{installing ? t("comm.installing") : t("comm.installToLibrary")}</span>
                </button>
                <button
                  className={`flex w-full items-center justify-center gap-1.5 rounded-xl border px-4 py-2 text-xs font-medium transition active:scale-[0.98] ${
                    listing.viewerBookmarked
                      ? "bg-accent/10 border-accent text-accent-soft"
                      : "border-line text-ink-soft hover:bg-bg-raised"
                  }`}
                  onClick={toggleBookmark}
                  disabled={working}
                >
                  <Bookmark size={13} className={listing.viewerBookmarked ? "fill-current" : ""} />
                  <span>{listing.viewerBookmarked ? t("comm.unbookmark") : t("comm.bookmark")}</span>
                </button>
              </div>

              {/* Stats Table */}
              <div className="border-t border-line/40 pt-3 space-y-2 text-xs">
                <div className="flex justify-between py-1 border-b border-line/20">
                  <span className="text-ink-faint">구분</span>
                  <span>{renderSubtypeBadge(listing, locale)}</span>
                </div>
                <div className="flex justify-between py-1 border-b border-line/20">
                  <span className="text-ink-faint">조회수</span>
                  <span className="font-medium text-ink-soft">{listing.viewCount}</span>
                </div>
                <div className="flex justify-between py-1 border-b border-line/20">
                  <span className="text-ink-faint">다운로드</span>
                  <span className="font-medium text-ink-soft">{listing.downloadCount}</span>
                </div>
                <div className="flex justify-between py-1 border-b border-line/20">
                  <span className="text-ink-faint">북마크</span>
                  <span className="font-medium text-ink-soft">{listing.bookmarkCount}</span>
                </div>
                <div className="flex justify-between py-1">
                  <span className="text-ink-faint">평점</span>
                  <span className="font-medium text-ink-soft flex items-center gap-0.5">
                    <Star size={11} className="text-yellow-400 fill-current" />
                    {listing.ratingAvg != null ? listing.ratingAvg.toFixed(1) : "-"} ({listing.ratingCount})
                  </span>
                </div>
              </div>

              {/* 평점 주기 */}
              <div className="border-t border-line/40 pt-3">
                <div className="mb-1.5 text-[11px] font-semibold text-ink-faint">{t("comm.myRating")}</div>
                <div className="flex flex-col gap-1">
                  <div className="flex gap-1.5">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button
                        key={n}
                        aria-label={t("comm.starsAria", { n })}
                        className={[
                          "transition transform hover:scale-110 active:scale-90",
                          n <= myStars ? "text-yellow-400" : "text-ink-faint hover:text-ink-soft",
                        ].join(" ")}
                        onClick={() => void rate(n)}
                      >
                        <Star size={20} fill={n <= myStars ? "currentColor" : "none"} aria-hidden="true" />
                      </button>
                    ))}
                  </div>
                  {!profile && <span className="text-[10px] text-ink-faint">{t("comm.loginRequired")}</span>}
                </div>
              </div>
            </div>

            {/* 소유자 도구 */}
            {profile && profile.id === listing.author.id && (
              <div className="rounded-2xl border border-line bg-bg-soft/40 p-3 space-y-2">
                <div className="text-[10px] font-semibold text-ink-faint mb-1">소유자 관리 도구</div>
                <div className="flex gap-2">
                  <button className="flex-1 rounded-lg bg-bg-raised border border-line px-2 py-1.5 text-[11px] text-ink-faint cursor-not-allowed" disabled>
                    {t("comm.edit")}
                  </button>
                  <button className="flex-1 rounded-lg bg-bg-raised border border-line px-2 py-1.5 text-[11px] text-ink-faint cursor-not-allowed" disabled>
                    {t("comm.versionUp")}
                  </button>
                </div>
                <button
                  className="w-full rounded-lg bg-red-500/10 border border-red-500/20 py-1.5 text-[11px] font-medium text-red-400 hover:bg-red-500/20 active:scale-95 transition"
                  onClick={unpublish}
                  disabled={working}
                >
                  {t("comm.unpublish")}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}

export function Modal({
  title,
  onClose,
  children,
  size = "lg",
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  size?: "md" | "lg" | "xl" | "2xl" | "3xl";
}) {
  const { t } = useT();
  useEscapeToClose(onClose);

  const sizeClasses = {
    md: "max-w-md",
    lg: "max-w-lg",
    xl: "max-w-xl",
    "2xl": "max-w-2xl",
    "3xl": "max-w-3xl",
  };

  return (
    <div className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-black/60 p-0 sm:p-4 backdrop-blur-sm transition-opacity duration-200">
      <div className={`flex max-h-screen max-h-[100dvh] w-full ${sizeClasses[size]} flex-col rounded-t-2xl bg-bg border border-line sm:max-h-[calc(100vh-2rem)] sm:max-h-[calc(100dvh-2rem)] sm:rounded-2xl shadow-2xl`}>
        <div className="flex h-12 shrink-0 items-center justify-between gap-2 border-b border-line px-4 bg-bg-soft/40">
          <h2 className="truncate text-sm font-bold text-ink tracking-tight">{title}</h2>
          <button
            aria-label={t("comm.close")}
            className="rounded p-1.5 text-ink-faint hover:bg-bg-raised hover:text-ink transition"
            onClick={onClose}
          >
            <X size={16} aria-hidden="true" />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-5 scroll-touch">{children}</div>
      </div>
    </div>
  );
}


function AdminPanelDialog({ onClose }: { onClose: () => void }) {
  const { t } = useT();
  const [users, setUsers] = useState<import("@roqeet/core").AdminUserInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    hubClient.adminListUsers(100, 0)
      .then(setUsers)
      .catch(e => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, []);

  const removeListing = async (listingId: string) => {
    if (!confirm("정말 이 발행물을 삭제하시겠습니까?")) return;
    try {
      await hubClient.adminRemoveListing(listingId);
      alert("삭제되었습니다.");
    } catch (e) {
      alert(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <Modal onClose={onClose} title={t("comm.admin.title")}>
      {loading ? (
        <p className="p-4 text-sm text-ink-faint">로딩 중...</p>
      ) : error ? (
        <p className="p-4 text-sm text-red-300">{error}</p>
      ) : (
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-ink">{t("comm.admin.users")}</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs text-ink-soft">
              <thead className="bg-bg-raised text-ink">
                <tr>
                  <th className="p-2">Handle</th>
                  <th className="p-2">Email</th>
                  <th className="p-2">IP/LastSignIn</th>
                  <th className="p-2">Listings</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {users.map(u => (
                  <tr key={u.userId}>
                    <td className="p-2">{u.handle} {u.isAdmin ? "(Admin)" : ""}</td>
                    <td className="p-2">{u.email}</td>
                    <td className="p-2">{u.lastSignInAt ? new Date(u.lastSignInAt).toLocaleString() : "-"}</td>
                    <td className="p-2">{u.listingCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-4 border-t border-line pt-4">
            <h3 className="text-sm font-medium text-ink">긴급 발행물 삭제 (강제)</h3>
            <div className="mt-2 flex gap-2">
              <input type="text" id="admin_remove_listing_id" placeholder="Listing ID" className="flex-1 rounded border border-line bg-bg-soft px-2 py-1 text-xs text-ink" />
              <button 
                className="rounded bg-red-500/10 px-3 py-1 text-xs text-red-400 hover:bg-red-500/20"
                onClick={() => {
                  const el = document.getElementById("admin_remove_listing_id") as HTMLInputElement;
                  if (el.value) removeListing(el.value);
                }}
              >
                {t("comm.admin.removeListing")}
              </button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
